import React, { useContext, useMemo } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';
import { Body as BodyBase } from 'js/design_system/Body';
import { DeprecatedBreakpoint } from 'js/design_system/DeprecatedBreakpoint';
import { NewsPreview } from 'js/social/shared/types';
import PaperContainerBase from './PaperContainer';
import { PostImage } from './shared/PostImage';
import { SocialEditor } from 'js/social/shared/SocialEditor/SocialEditor';
import useBreakpoint from 'js/utils/hooks/useDeprecatedBreakpoint';
import News from 'js/social/News/News';
import { FeedContext } from 'js/social/contexts/feed';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Name = styled(BodyBase)`
  color: $ds-gray-dark;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;

  @include for-tablet-up {
    flex-direction: row;
    gap: 16px;
  }
`;

const LeftContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 100%;
`;

const QuotedPostContainer = styled.div`
  margin: 4px 0;
  border: 1px solid $ds-gray-light;
  border-radius: 4px;
`;

const ImageContainer = styled.div`
  flex-shrink: 0;
  margin: 4px 0;

  @include for-tablet-up {
    width: 132px;
  }
`;

const PaperContainer = styled(PaperContainerBase)`
  margin: 4px 0;
`;

const StatsBody = styled(BodyBase)`
  color: $ds-gray-dark;
  white-space: pre;
`;

type Props = {
  displayName: string;
  preview: NewsPreview;
  onsitePodcastPromotion?: boolean;
};

export const CollapsedActivityPreview: React.FC<Props> = ({
  displayName,
  preview: {
    content,
    tags,
    hashtags,
    quotable,
    work,
    image,
    reason,
    numLikes,
    numComments,
  },
  onsitePodcastPromotion = false,
}) => {
  const likeText =
    numLikes > 0 ? `${numLikes} ${pluralize('like', numLikes)}` : null;
  const commentText =
    numComments > 0
      ? `${numComments} ${pluralize('comment', numComments)}`
      : null;
  const statsText = [likeText, commentText].filter(Boolean).join(`  •  `);
  const {
    userActions: { getUserData },
  } = useContext(FeedContext);
  const hasContent = !!content || tags.length > 0;
  const mobile = useBreakpoint() < DeprecatedBreakpoint.tablet;

  const imageComponent = useMemo(
    () =>
      image ? (
        <ImageContainer>
          <PostImage title={displayName} caption={content} image={image} />
        </ImageContainer>
      ) : null,
    [content, image, displayName],
  );

  return (
    <Container>
      {reason !== 'promotion' && (
        <Name bold>
          {displayName} {reason}
        </Name>
      )}

      <ContentContainer>
        <LeftContainer>
          {hasContent && (
            <SocialEditor
              readOnly
              collapsible
              content={content ?? ''}
              tags={tags}
              allowedHashtags={hashtags.map((hashtag) => hashtag.hashtag)}
              lines={2}
            />
          )}

          {(mobile || !hasContent) && imageComponent}

          {work && (
            <PaperContainer
              work={work}
              style={{ padding: 0 }}
              onsitePodcastPromotion={onsitePodcastPromotion}
            />
          )}

          {quotable && (
            <QuotedPostContainer>
              <News
                {...quotable}
                getUserData={getUserData}
                setUserFollowing={() => null}
                removeUserContent={() => null}
                removeFeedNews={() => null}
                addFeedNews={() => null}
                addFeedComment={() => null}
                updateFeedNews={() => null}
                likeFeedNews={() => null}
                unlikeFeedNews={() => null}
                repostFeedNews={() => null}
                unrepostFeedNews={() => null}
                updateWorkBookmarkStatus={() => null}
              />
            </QuotedPostContainer>
          )}

          {statsText && <StatsBody size="sm">{statsText}</StatsBody>}
        </LeftContainer>

        {!mobile && hasContent && imageComponent}
      </ContentContainer>
    </Container>
  );
};
